import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Award, Download, Eye, ArrowUpRight } from 'lucide-react'
import { getMyCertificates, type CertificateResult } from '@/services/certificateService'

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })

export default function CertificatesTab() {
  const [certificates, setCertificates] = useState<CertificateResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    getMyCertificates()
      .then(setCertificates)
      .catch((err) => setError(err instanceof Error ? err.message : 'โหลดใบประกาศไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <p className="mb-6 text-sm text-[#69738A]">ใบประกาศทั้งหมดที่คุณได้รับจากการเรียนจบคอร์ส ({certificates.length})</p>

      {loading ? (
        <p className="text-sm text-[#69738A]">กำลังโหลด...</p>
      ) : error ? (
        <p className="text-sm text-rose-500">{error}</p>
      ) : certificates.length === 0 ? (
        <div className="border border-[#E7E7ED] bg-white p-12 text-center" style={{ borderRadius: 6 }}>
          <Award className="mx-auto mb-3 h-8 w-8 text-[#69738A]/40" strokeWidth={1.5} />
          <p className="mb-4 text-[#69738A]">คุณยังไม่มีใบประกาศ — เรียนคอร์สที่มีใบประกาศให้จบเพื่อรับใบประกาศแรกของคุณ</p>
          <Link to="/account?tab=courses" className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline">
            ไปดูคอร์สของฉัน <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <div className="divide-y divide-[#E7E7ED] border border-[#E7E7ED] bg-white" style={{ borderRadius: 6 }}>
          {certificates.map((cert) => (
            <div key={cert.certificate_id} className="flex items-center gap-5 p-5 transition-colors hover:bg-[#F7F6FB]">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center border border-[#E7E7ED] bg-[#F7F6FB]" style={{ borderRadius: 4 }}>
                <Award className="h-6 w-6 text-accent" strokeWidth={1.5} />
              </div>

              <div className="min-w-0 flex-1">
                <p className="line-clamp-1 text-[15px] font-medium text-[#19172A]">{cert.course_title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#69738A]">
                  <span>ออกให้เมื่อ {formatDate(cert.issued_at)}</span>
                  <span className="hidden sm:inline text-[#E7E7ED]">|</span>
                  <span>เลขที่ {cert.certificate_number}</span>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <Link
                  to={`/certificates/${cert.enrollment_id}`}
                  className="group/btn flex items-center gap-1.5 border border-[#E7E7ED] px-3 py-2 text-xs font-medium text-[#19172A] transition-colors hover:border-accent/40 hover:text-accent"
                  style={{ borderRadius: 4 }}
                >
                  <Eye className="h-3.5 w-3.5" />
                  ดูใบประกาศ
                </Link>
                <Link
                  to={`/certificates/${cert.enrollment_id}`}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[#69738A] transition-colors hover:text-accent"
                >
                  <Download className="h-3.5 w-3.5" />
                  ดาวน์โหลด
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
